import React, { useMemo } from 'react'
import { dash, formatFleetValue, isUnitBillable, normalizeFleetUnit } from '../fleetLiveHelpers'
import PumpDetailPanel from './PumpDetailPanel'

const getChannelEntries = (unit) => {
  const channels = Array.isArray(unit?.channels) ? unit.channels : []
  if (channels.length) {
    return channels.map((channel, index) => {
      if (channel == null) {
        return { key: `ch-${index}`, label: `Channel ${index + 1}`, value: null }
      }
      if (typeof channel === 'number' || typeof channel === 'string') {
        return {
          key: `ch-${index}`,
          label: `Channel ${index + 1}`,
          value: channel,
        }
      }
      const label = channel.name
        || channel.Name
        || channel.channelName
        || channel.ChannelName
        || `Channel ${index + 1}`
      const value = channel.value ?? channel.Value ?? channel.val ?? null
      return {
        key: `${label}-${index}`,
        label,
        value,
      }
    })
  }

  const fields = Array.isArray(unit?.fields) ? unit.fields.filter((field) => !field?.isCustom) : []
  const dashboardFields = fields.filter((field) => field?.isDashboardField)
  const source = dashboardFields.length ? dashboardFields : fields
  return [...source]
    .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
    .map((field, index) => ({
      key: `${field.fieldName || 'field'}-${index}`,
      label: field.fieldName || `Field ${index + 1}`,
      value: field.value,
    }))
}

const formatChannelCell = (value) => {
  if (value == null || value === '') return dash
  if (typeof value === 'number') return formatFleetValue(value, 2)
  const asNumber = Number(value)
  if (!Number.isNaN(asNumber) && String(value).trim() !== '') {
    return formatFleetValue(asNumber, 2)
  }
  return String(value)
}

const GenericFleetTable = ({
  units = [],
  selectedUnitUId,
  detailUnit,
  onSelectUnit,
  onCloseDetail,
  waitingLabel = 'Waiting for data…',
  mappingsHint = 'No channel mappings initialized',
  unbillableLabel = 'Not in subscription',
}) => {
  const columns = useMemo(() => {
    const labels = []
    const seen = new Set()
    units.forEach((unit) => {
      getChannelEntries(unit).forEach((entry) => {
        if (!seen.has(entry.label)) {
          seen.add(entry.label)
          labels.push(entry.label)
        }
      })
    })
    return labels.length ? labels : ['Value']
  }, [units])

  const selectedUnit = useMemo(() => {
    if (!selectedUnitUId) return null
    if (detailUnit && detailUnit.unitUId === selectedUnitUId) {
      return normalizeFleetUnit(detailUnit)
    }
    return units.find((u) => u.unitUId === selectedUnitUId) || null
  }, [selectedUnitUId, detailUnit, units])

  const isDetailLoading = Boolean(
    selectedUnitUId
    && (!detailUnit || detailUnit.unitUId !== selectedUnitUId)
  )

  if (!units.length) {
    return (
      <div className="fleetEmptyState">
        {waitingLabel}
        <div className="fleetEmptyHint">{mappingsHint}</div>
      </div>
    )
  }

  return (
    <>
      <div className="fleetTableWrap">
        <table className="fleetLiveTable">
          <thead>
            <tr>
              <th>Unit</th>
              {columns.map((label) => (
                <th key={label}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {units.map((unit) => {
              const byLabel = {}
              getChannelEntries(unit).forEach((entry) => {
                byLabel[entry.label] = entry.value
              })
              const isSelected = selectedUnitUId === unit.unitUId
              const billable = isUnitBillable(unit)
              return (
                <tr
                  key={unit.unitUId}
                  className={`${isSelected ? 'is-selected' : ''}${!billable ? ' is-unbillable' : ''}`}
                  title={!billable ? unbillableLabel : undefined}
                  onClick={billable ? () => onSelectUnit?.(unit.unitUId, unit.licenseId) : undefined}
                  style={{ cursor: onSelectUnit && billable ? 'pointer' : undefined }}
                >
                  <td className="fleetLiveTableUnit">
                    {unit.unitName || unit.unitUId}
                  </td>
                  {columns.map((label) => (
                    <td key={label} className="fleetLiveTableNum">
                      {formatChannelCell(byLabel[label])}
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {selectedUnitUId ? (
        <PumpDetailPanel
          unit={selectedUnit || { unitUId: selectedUnitUId }}
          isLoading={isDetailLoading}
          onClose={onCloseDetail}
        />
      ) : null}
    </>
  )
}

export default GenericFleetTable
